import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError, of, forkJoin } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

// Models matching backend ClientIntegrationDtos
// CRITICAL: Ensure these enum values exactly match the backend expectations
export enum ClientType {
  Ecom = 1,
  Sea = 2,
  Air = 3,
  Land = 4,
}

export enum ClientStatus {
  Active = 1,
  Inactive = 0,
  Suspended = -1,
  PendingVerification = 2 
}

export interface ClientIntegrationDto {
  id: string;
  clientName: string;
  clientType: ClientType;
  description?: string;
  status: ClientStatus;
  apiBaseUrl?: string;
  clientAccountId?: string;
  hasSecret: boolean;
  lastConnectionTest?: Date;
  lastConnectionSuccessful?: boolean;
  lastConnectionError?: string;
  createdAtUtc: Date;
  lastUpdatedAtUtc?: Date;
  createdBy?: string;
  lastModifiedBy?: string;
  endpoints: ClientEndpointDto[];
  linkedUsers: ClientUserDto[];
  
  // ===== INBOUND CREDENTIALS FIELDS =====
  // Fields for handling credentials that clients use to authenticate with our system
  expectedAccountFromClient?: string;      // Account identifier we expect from the client
  hasInboundSecret?: boolean;             // Whether client has secret to authenticate to us
  environment?: string;                   // Environment setting (dev, staging, prod)
  
  // ===== OUTBOUND CREDENTIALS FIELDS =====
  // Fields for our credentials to authenticate with the client's system  
  ourAccountForClient?: string;           // Our account identifier for client's system
  hasOurSecretForClient?: boolean;        // Whether we have secret to authenticate to client
  
  // ===== CONFIGURATION FIELDS =====
  // Fields for client configuration and priority handling
  isDefaultForClientType?: boolean;       // Whether this is the default client for its type
  configurationPriority?: number;         // Priority level for configuration (higher = more priority)
  lastIntegrationTest?: Date;             // Timestamp of last integration test  
  lastIntegrationTestSuccessful?: boolean; // Whether last integration test was successful
}

export interface ClientEndpointDto {
  id: string;
  endpointName: string;
  endpointPath: string;
  httpMethod: string;
  isActive: boolean;
  // DYNAMIC INTEGRATION: Add system feature mapping
  systemFeature?: 'repack_update' | 'customs_clearance' | 'order_status' | 'delivery_tracking' | 'pre_alert' | 'customs_manifest' | 'push_info' | 'push_weight' | 'cancel_order';
}

export interface ClientUserDto {
  id: string;
  userId: number;
  username: string;
  permissions: string[];
  linkedAtUtc: Date;
  linkedBy: string;
}

export interface ClientAuditLogDto {
  id: number;
  action: string;
  details?: string;
  success: boolean;
  performedAtUtc: Date;
  performedBy: string;
}

export interface CreateClientIntegrationDto {
  clientName: string;
  clientType: ClientType;
  description?: string;
  apiBaseUrl: string;
  expectedAccountFromClient: string;
  OurSecretForClient: string;
  endpoints: ClientEndpointDto[];
  // CRITICAL FIX: Add required backend fields to prevent 400 error
  inboundSecret: string;
}

export interface UpdateClientIntegrationDto {
  clientName: string;
  description?: string;
  apiBaseUrl: string;
  clientAccountId: string;
  endpoints: ClientEndpointDto[];
}

export interface ClientSecretsDto {
  secretKey: string;
  testConnection?: boolean;
}

export interface EnhancedClientSecretsDto {
  credentialDirection?: 'INBOUND' | 'OUTBOUND';
  credentialType?: string;
  secretKey: string;
  additionalData?: any;
  testConnection?: boolean;
}

export interface ConnectionTestResult {
  success: boolean;
  message: string;
  testedAt: Date;
  responseTime?: any;
}

export interface CreateClientEndpointDto {
  id: string;
  endpointName: string;
  endpointPath: string;
  httpMethod: string;
  isActive: boolean;
  // DYNAMIC INTEGRATION: Add system feature mapping
  systemFeature?: 'repack_update' | 'customs_clearance' | 'order_status' | 'delivery_tracking' | 'pre_alert' | 'customs_manifest' | 'push_info' | 'push_weight' | 'cancel_order';
}

export interface CreateClientUserDto {
  userId: string;
  permissions: string[];
}

export interface ClientApiCredentials {
  clientId: string;
  clientName: string;
  apiAccount: string;
  apiSecretKey: string;
  allowedEndpoints: string[];
  isActive: boolean;
  createdAtUtc: Date;
  createdBy: string;
}

export interface ClientCredentialsDto {
  // ===== BASIC CREDENTIAL INFORMATION =====
  apiAccount: string;
  allowedEndpoints: string[];
  isActive: boolean;
  createdAtUtc: Date;
  note?: string;
  
  // ===== COMPREHENSIVE CREDENTIAL DETAILS =====
  // Authentication method and configuration
  authMethod?: 'api_key' | 'jwt' | 'oauth2' | 'basic_auth' | 'bearer_token' | 'custom';
  credentialType?: 'INBOUND' | 'OUTBOUND' | 'BIDIRECTIONAL';
  
  // Environment and configuration
  environment?: string;                    // dev, staging, prod
  configurationPriority?: number;         // Priority level for this credential set
  
  // Connection details
  connectionTimeout?: number;              // Timeout in seconds
  lastConnectionTest?: Date;              // Timestamp of last connection test
  lastConnectionSuccessful?: boolean;     // Whether last test was successful
  lastConnectionError?: string;           // Error from last connection test
  
  // Credential metadata
  expirationDate?: Date;                  // When credentials expire
  lastUpdatedAtUtc?: Date;               // When credentials were last updated
  lastUsedAtUtc?: Date;                  // When credentials were last used
  
  // Usage tracking
  requestCount?: number;                  // Number of requests made with these credentials
  successfulRequestCount?: number;       // Number of successful requests
  lastRequestAtUtc?: Date;               // Timestamp of last request
  
  // Security and validation
  isDefault?: boolean;                   // Whether this is the default credential set
  isRevoked?: boolean;                   // Whether credentials have been revoked
  revokedAtUtc?: Date;                  // When credentials were revoked
  revokedBy?: string;                   // Who revoked the credentials
  revokedReason?: string;               // Reason for revocation
}

export interface GenerateCredentialsResponse {
  apiAccount: string;
  apiSecretKey: string;
  allowedEndpoints: string[];
  usage?: string;
  exampleJwtHeader?: {
    alg: string;
    typ: string;
    account: string;
  };
}

@Injectable({
  providedIn: 'root'
})
export class IntegrationService {
  private readonly apiUrl = `${environment.apiUrl}/integration-clients`;

  constructor(private http: HttpClient) {}

  /**
   * Get all client integrations
   */
  getClientIntegrations(searchTerm: string = '', pageNumber: number = 1, pageSize: number = 50): Observable<ClientIntegrationDto[]> {
    const params = new URLSearchParams();
    params.append('SearchTerm', searchTerm);
    params.append('PageNumber', pageNumber.toString());
    params.append('PageSize', pageSize.toString());

    return this.http.get<ClientIntegrationDto[]>(`${this.apiUrl}?${params.toString()}`)
      .pipe(
        map(response => {
          // Backend now returns a simple array, not paginated response
          const clients = Array.isArray(response) ? response : [response];
          return clients.map((client: any) => ({
            ...client,
            createdAtUtc: new Date(client.createdAtUtc),
            lastUpdatedAtUtc: client.lastUpdatedAtUtc ? new Date(client.lastUpdatedAtUtc) : undefined,
            lastConnectionTest: client.lastConnectionTest ? new Date(client.lastConnectionTest) : undefined,
            // Map additional integration test fields
            lastIntegrationTest: client.lastIntegrationTest ? new Date(client.lastIntegrationTest) : undefined,
            // Handle both camelCase and PascalCase from backend
            expectedAccountFromClient: client.expectedAccountFromClient || client.ExpectedAccountFromClient,
            hasInboundSecret: client.hasInboundSecret ?? client.HasInboundSecret ?? false,
            environment: client.environment || client.Environment,
            ourAccountForClient: client.ourAccountForClient || client.OurAccountForClient,
            hasOurSecretForClient: client.hasOurSecretForClient ?? client.HasOurSecretForClient ?? false,
            isDefaultForClientType: client.isDefaultForClientType ?? client.IsDefaultForClientType ?? false,
            configurationPriority: client.configurationPriority ?? client.ConfigurationPriority ?? 0,
            lastIntegrationTestSuccessful: client.lastIntegrationTestSuccessful ?? client.LastIntegrationTestSuccessful
          }));
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Get a specific client integration by ID
   */
  getClientIntegration(id: string): Observable<ClientIntegrationDto> {
    return this.http.get<any>(`${this.apiUrl}/${id}`)
      .pipe(
        map(response => {
          const client = response.data || response;
          return {
            ...client,
            createdAtUtc: new Date(client.createdAtUtc),
            lastUpdatedAtUtc: client.lastUpdatedAtUtc ? new Date(client.lastUpdatedAtUtc) : undefined,
            lastConnectionTest: client.lastConnectionTest ? new Date(client.lastConnectionTest) : undefined,
            // Map additional integration test fields
            lastIntegrationTest: client.lastIntegrationTest ? new Date(client.lastIntegrationTest) : undefined,
            // Handle both camelCase and PascalCase from backend
            expectedAccountFromClient: client.expectedAccountFromClient || client.ExpectedAccountFromClient,
            hasInboundSecret: client.hasInboundSecret ?? client.HasInboundSecret ?? false,
            environment: client.environment || client.Environment,
            ourAccountForClient: client.ourAccountForClient || client.OurAccountForClient,
            hasOurSecretForClient: client.hasOurSecretForClient ?? client.HasOurSecretForClient ?? false,
            isDefaultForClientType: client.isDefaultForClientType ?? client.IsDefaultForClientType ?? false,
            configurationPriority: client.configurationPriority ?? client.ConfigurationPriority ?? 0,
            lastIntegrationTestSuccessful: client.lastIntegrationTestSuccessful ?? client.LastIntegrationTestSuccessful
          };
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Create new client integration
   */
  createClientIntegration(data: CreateClientIntegrationDto): Observable<any> {
    // DEBUG: Log the request being sent to help identify 400 error
    console.log('🚀 [createClientIntegration] Sending POST request to:', this.apiUrl);
    console.log('📤 [createClientIntegration] Request payload:', JSON.stringify(data, null, 2));
    
    return this.http.post<any>(this.apiUrl, data)
      .pipe(
        map(response => {
          console.log('✅ [createClientIntegration] Success response:', response);
          return response;
        }),
        catchError(error => {
          console.error('❌ [createClientIntegration] Error response:', {
            status: error.status,
            statusText: error.statusText,
            url: error.url,
            error: error.error,
            message: error.message
          });
          return this.handleError(error);
        })
      );
  }

  /**
   * Update client integration
   */
  updateClientIntegration(id: string, data: UpdateClientIntegrationDto): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/${id}`, data)
      .pipe(
        catchError(this.handleError)
      );
  }

  /**
   * Delete client integration
   */
  deleteClientIntegration(id: string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/${id}`)
      .pipe(catchError(this.handleError));
  }

  /**
   * Test connection to client integration
   */
  testConnection(id: string): Observable<ConnectionTestResult> {
    return this.http.post<any>(`${this.apiUrl}/${id}/test-connection`, {})
      .pipe(
        map(response => {
          const result = response.data || response;
          return {
            ...result,
            testedAt: new Date(result.testedAt)
          };
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Update client secrets (secure)
   */
  updateClientSecrets(id: string, secrets: ClientSecretsDto | EnhancedClientSecretsDto): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/${id}/secret`, secrets)
      .pipe(catchError(this.handleError));
  }

  /**
   * Update enhanced client secrets with credential direction support
   * Routes to the appropriate backend endpoint based on credential direction
   * CRITICAL: Automatically detects whether to use POST (create) or PUT (update) for outbound credentials
   */
  updateEnhancedClientSecrets(id: string, secrets: EnhancedClientSecretsDto, hasExistingOutboundCredentials?: boolean): Observable<any> {
    // Handle OUTBOUND credentials differently
    if (secrets.credentialDirection === 'OUTBOUND') {
      // Map to the expected backend format for outbound credentials
      // Note: Backend expects PascalCase for these fields
      const outboundData = {
        OurAccountForClient: secrets.additionalData?.clientAccount || '',
        OurSecretForClient: secrets.secretKey,
        ClientApiBaseUrl: secrets.additionalData?.clientBaseUrl || '',
        TestAfterUpdate: secrets.testConnection || false,
        TestAfterCreation: secrets.testConnection || false // For POST endpoint
      };
      
      // CRITICAL FIX: Determine whether to use POST or PUT based on existing credentials
      // If hasExistingOutboundCredentials is not provided, default to PUT for backward compatibility
      const httpMethod = hasExistingOutboundCredentials === false ? 'POST' : 'PUT';
      const endpoint = `${this.apiUrl}/${id}/credentials/outbound`;
      
      console.log('Sending OUTBOUND credentials request:', {
        clientId: id,
        method: httpMethod,
        endpoint: endpoint,
        hasExistingCredentials: hasExistingOutboundCredentials,
        payload: outboundData
      });
      
      // Use the correct HTTP method based on whether credentials exist
      if (httpMethod === 'POST') {
        // Creating new outbound credentials
        return this.http.post<any>(endpoint, outboundData)
          .pipe(
            catchError(error => {
              // If POST fails with 409 (Conflict), it means credentials already exist
              // Retry with PUT
              if (error.status === 409) {
                console.log('POST failed with 409 Conflict, retrying with PUT...');
                return this.http.put<any>(endpoint, outboundData)
                  .pipe(catchError(this.handleError));
              }
              return this.handleError(error);
            })
          );
      } else {
        // Updating existing outbound credentials
        return this.http.put<any>(endpoint, outboundData)
          .pipe(
            catchError(error => {
              // If PUT fails with 404, it means no credentials exist
              // Retry with POST
              if (error.status === 404 && error.error?.retcode === -1203003) {
                console.log('PUT failed with 404 Not Found, retrying with POST...');
                return this.http.post<any>(endpoint, outboundData)
                  .pipe(catchError(this.handleError));
              }
              return this.handleError(error);
            })
          );
      }
    }
    
    // For INBOUND or other credential types, use the regular secret endpoint
    return this.http.put<any>(`${this.apiUrl}/${id}/secret`, secrets)
      .pipe(catchError(this.handleError));
  }

  /**
   * Link user to client
   */
  linkUserToClient(clientId: string, userData: CreateClientUserDto): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/${clientId}/users`, userData)
      .pipe(catchError(this.handleError));
  }

  /**
   * Get client users
   */
  getClientUsers(clientId: string): Observable<ClientUserDto[]> {
    return this.http.get<any>(`${this.apiUrl}/${clientId}/users`)
      .pipe(
        map(response => {
          const items = response.items || response;
          return items.map((user: any) => ({
            ...user,
            linkedAtUtc: new Date(user.linkedAtUtc)
          }));
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Remove user from client
   */
  removeLinkedUser(clientId: string, userId: string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/${clientId}/users/${userId}`)
      .pipe(catchError(this.handleError));
  }

  /**
   * Get client audit logs
   */
  getClientAuditLogs(clientId: string, page: number = 1, pageSize: number = 20): Observable<ClientAuditLogDto[]> {
    return this.http.get<any>(`${this.apiUrl}/${clientId}/audit-logs?page=${page}&pageSize=${pageSize}`)
      .pipe(
        map(response => {
          const items = response.items || response;
          return items.map((log: any) => ({
            ...log,
            performedAtUtc: new Date(log.performedAtUtc)
          }));
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Generate client API credentials
   */
  generateClientCredentials(clientId: string): Observable<GenerateCredentialsResponse> {
    return this.http.post<any>(`${this.apiUrl}/${clientId}/generate-credentials`, {})
      .pipe(
        map(response => {
          const data = response.data || response;
          return {
            ...data,
            createdAtUtc: data.createdAtUtc ? new Date(data.createdAtUtc) : new Date()
          };
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Get client API credentials (without secret key)
   */
  getClientCredentials(clientId: string): Observable<ClientCredentialsDto> {
    return this.http.get<any>(`${this.apiUrl}/${clientId}/credentials`)
      .pipe(
        map(response => {
          const data = response.data || response;
          return {
            ...data,
            createdAtUtc: new Date(data.createdAtUtc)
          };
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Regenerate client API credentials
   */
  regenerateClientCredentials(clientId: string): Observable<GenerateCredentialsResponse> {
    return this.http.post<any>(`${this.apiUrl}/${clientId}/regenerate-credentials`, {})
      .pipe(
        map(response => {
          const data = response.data || response;
          return {
            ...data,
            createdAtUtc: data.createdAtUtc ? new Date(data.createdAtUtc) : new Date()
          };
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Get enabled features for a specific client
   * Returns the system features that are enabled based on the client's endpoint mappings
   */
  getClientEnabledFeatures(clientId: string): Observable<string[]> {
    return this.http.get<{ success: boolean; enabledFeatures: string[]; message?: string }>(`${this.apiUrl}/${clientId}/enabled-features`)
      .pipe(
        map(response => {
          if (!response.success) {
            throw new Error(response.message || 'Failed to fetch enabled features');
          }
          return response.enabledFeatures;
        }),
        catchError(this.handleError)
      );
  }

  /**
   * Check if a specific feature is enabled for a client
   */
  isClientFeatureEnabled(clientId: string, feature: string): Observable<boolean> {
    return this.getClientEnabledFeatures(clientId).pipe(
      map(enabledFeatures => enabledFeatures.includes(feature)),
      catchError(error => {
        console.error(`Error checking feature ${feature} for client ${clientId}:`, error);
        return of(false);
      })
    );
  }

  /**
   * Get client integration with enabled features
   * Enhanced version that includes feature information
   */
  getClientIntegrationWithFeatures(id: string): Observable<ClientIntegrationDto & { enabledFeatures: string[] }> {
    return this.getClientIntegration(id).pipe(
      switchMap(integration => 
        this.getClientEnabledFeatures(id).pipe(
          map(enabledFeatures => ({
            ...integration,
            enabledFeatures
          })),
          catchError(error => {
            console.warn('Failed to fetch enabled features, returning integration without features:', error);
            return of({
              ...integration,
              enabledFeatures: []
            });
          })
        )
      )
    );
  }

  /**
   * Create a new endpoint for a client
   */
  createEndpoint(clientId: string, endpoint: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/${clientId}/endpoints`, endpoint)
      .pipe(catchError(this.handleError));
  }

  /**
   * Delete an endpoint from a client
   */
  deleteEndpoint(clientId: string, endpointId: string): Observable<any> {
    return this.http.delete<any>(`${this.apiUrl}/${clientId}/endpoints/${endpointId}`)
      .pipe(catchError(this.handleError));
  }

  /**
   * Update client endpoint with system feature mapping
   */
  updateClientEndpoint(clientId: string, endpointId: string, endpoint: Partial<ClientEndpointDto>): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/${clientId}/endpoints/${endpointId}`, endpoint)
      .pipe(catchError(this.handleError));
  }

  /**
   * Bulk update endpoint mappings for a client
   */
  bulkUpdateEndpoints(clientId: string, endpointMappings: Array<{endpointId: string, systemFeature: string | null}>): Observable<any> {
    const requestBody = {
      endpointMappings: endpointMappings
    };
    
    console.log('🔄 [bulkUpdateEndpoints] Sending bulk update request:', {
      clientId,
      url: `${this.apiUrl}/${clientId}/endpoints/bulk`,
      payload: requestBody
    });
    
    return this.http.put<any>(`${this.apiUrl}/${clientId}/endpoints/bulk`, requestBody)
      .pipe(
        map(response => {
          console.log('✅ [bulkUpdateEndpoints] Success response:', response);
          return response;
        }),
        catchError(error => {
          console.error('❌ [bulkUpdateEndpoints] Error response:', {
            status: error.status,
            statusText: error.statusText,
            url: error.url,
            error: error.error,
            message: error.message
          });
          return this.handleError(error);
        })
      );
  }

  /**
   * Get all clients with their enabled features
   * Useful for admin interfaces showing feature overview
   */
  getAllClientsWithFeatures(): Observable<Array<ClientIntegrationDto & { enabledFeatures: string[] }>> {
    return this.getClientIntegrations().pipe(
      switchMap(clients => {
        // Get features for each client in parallel
        const clientsWithFeatures$ = clients.map(client =>
          this.getClientEnabledFeatures(client.id).pipe(
            map(enabledFeatures => ({
              ...client,
              enabledFeatures
            })),
            catchError(error => {
              console.warn(`Failed to fetch features for client ${client.id}:`, error);
              return of({
                ...client,
                enabledFeatures: []
              });
            })
          )
        );
        
        // Wait for all feature requests to complete
        return forkJoin(clientsWithFeatures$);
      }),
      catchError(error => {
        console.error('Error fetching clients with features:', error);
        return of([]);
      })
    );
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: any): Observable<never> {
    console.error('An error occurred:', error);
    return throwError(() => error);
  }
}
